// ---------------------------------------------------------------------------
// Bell Operations Console (Super Admin)
// ---------------------------------------------------------------------------
// Day-wise / hour-wise bell playing status for a selected school. A superadmin
// picks a school and sees the current week's hourly bell status (Played / Late
// / Failed / Offline) by default, with week navigation, week KPIs, a per-event
// detail drawer, CSV export and EN/NE language toggle.
//
// This is a full React port of the standalone console: the original imperative
// DOM code now lives across constants / data / utils / services / components,
// and this page owns the state, effects and orchestration. All markup keeps the
// console's class names, which the scoped `.dgc` stylesheet (ConsoleStyles)
// paints without leaking into the surrounding Chakra dashboard.
// ---------------------------------------------------------------------------
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";

import {CONFIG, DAYS_EN} from "../../constants/BellStatusConstants";
import {addDays, fmtRange, iso, makeI18n, parseISO, weekStart} from "../../utils/bellStatusUtils";
import {fetchSchools, fetchWeek, ringBell} from "../../services/BellStatusService";

import ConsoleStyles from "../../components/ConsoleStyles";
import TopRail from "../../components/TopRail";
import DemoBanner from "../../components/DemoBanner";
import SchoolContext from "../../components/SchoolContext";
import WeekKpis from "../../components/WeekKpis";
import BellGrid from "../../components/BellGrid";
import EventDrawer from "../../components/EventDrawer";
import Toast from "../../components/Toast";
import LiveBellFeed from "../../components/LiveBellFeed";

function BellOperationsConsole(props = {}) {
    // Controllable: when the parent (BellStatusPage) owns schools / selected
    // school / language, those props drive the console; otherwise it falls back
    // to its own internal state so the console still works standalone.
    const {
        schools: schoolsProp,
        schoolId: schoolIdProp,
        onSchoolChange,
        lang: langProp,
        onToggleLang: onToggleLangProp,
        onBackToOverall,
    } = props;

    const [langState, setLangState] = useState(() => localStorage.getItem("dg-lang") || "en");
    const lang = langProp != null ? langProp : langState;

    const [schoolsState, setSchoolsState] = useState([]);
    const schools = schoolsProp != null ? schoolsProp : schoolsState;

    const [schoolIdState, setSchoolIdState] = useState(null);
    const schoolId = schoolIdProp != null ? schoolIdProp : schoolIdState;

    // Unified setters that respect controlled vs uncontrolled mode.
    const setSchoolId = onSchoolChange || setSchoolIdState;
    const controlledSchools = schoolsProp != null;

    const [anchor, setAnchor] = useState(() => weekStart(new Date()));
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [demoMode, setDemoMode] = useState(false);
    const [filter, setFilter] = useState(() => new Set());
    const [drawer, setDrawer] = useState(null); // {data, dateKey, periodId}
    const [toastMsg, setToastMsg] = useState("");
    const [lastFetch, setLastFetch] = useState("");

    const i18n = useMemo(() => makeI18n(lang), [lang]);
    const toastTimer = useRef(null);

    const showToast = useCallback((msg) => {
        setToastMsg(msg);
        clearTimeout(toastTimer.current);
        toastTimer.current = setTimeout(() => setToastMsg(""), 2400);
    }, []);

    /* ── load: schools (once) — only when the parent doesn't supply them ──── */
    useEffect(() => {
        if (controlledSchools) return undefined;
        let alive = true;
        (async () => {
            const {data: list, source} = await fetchSchools();
            if (!alive) return;
            setSchoolsState(list);
            setDemoMode(source === "mock");
            setSchoolIdState((prev) => (prev && list.some((s) => s.id === prev) ? prev : list[0] && list[0].id));
        })();
        return () => {
            alive = false;
        };
    }, [controlledSchools]);

    /* ── load: the visible week ─────────────────────────────────────────── */
    const loadWeek = useCallback(async () => {
        if (!schoolId) return;
        const from = iso(anchor), to = iso(addDays(anchor, 6));
        const schoolMeta = schools.find((s) => s.id === schoolId) || null;
        setLoading(true);
        try {
            const {data: week} = await fetchWeek(schoolId, from, to, lang, schoolMeta);
            setData(week);
            setDemoMode(false);
            setLastFetch(
                i18n.t("updated") + new Date().toLocaleTimeString([], {hour: "2-digit", minute: "2-digit"})
            );
        } catch (e) {
            // Real data only — no mock fallback. Surface the failure and clear
            // the grid so its empty/error state shows instead of stale data.
            setData(null);
            showToast("✕ " + i18n.t("errT"));
        } finally {
            setLoading(false);
        }
    }, [schoolId, anchor, lang, i18n, schools, showToast]);

    useEffect(() => {
        loadWeek();
    }, [loadWeek]);

    /* ── auto refresh while viewing the current week ────────────────────── */
    useEffect(() => {
        const id = setInterval(() => {
            if (anchor.getTime() === weekStart(new Date()).getTime() && !document.hidden) loadWeek();
        }, CONFIG.REFRESH_MS);
        return () => clearInterval(id);
    }, [anchor, loadWeek]);

    /* ── keyboard: ← / → weeks, Esc closes drawer ───────────────────────── */
    const canNext = anchor.getTime() < weekStart(new Date()).getTime();
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") setDrawer(null);
            const tag = e.target && e.target.tagName;
            if (tag === "SELECT" || tag === "INPUT") return;
            if (e.key === "ArrowLeft") setAnchor((a) => addDays(a, -7));
            if (e.key === "ArrowRight" && canNext) setAnchor((a) => addDays(a, 7));
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [canNext]);

    useEffect(() => () => clearTimeout(toastTimer.current), []);

    /* ── handlers ───────────────────────────────────────────────────────── */
    const toggleLang = () => {
        if (onToggleLangProp) {
            onToggleLangProp();
            return;
        }
        const next = lang === "en" ? "ne" : "en";
        setLangState(next);
        localStorage.setItem("dg-lang", next);
    };
    const toggleFilter = (f) =>
        setFilter((prev) => {
            const n = new Set(prev);
            n.has(f) ? n.delete(f) : n.add(f);
            return n;
        });
    const openDrawer = (dateKey, periodId) => setDrawer({data, dateKey, periodId});
    const onRing = async (periodId, dateKey) => {
        if (!CONFIG.DEMO) {
            try {
                await ringBell(schoolId, periodId, dateKey);
            } catch (err) {
                showToast("✕ " + err.message);
                return;
            }
        }
        showToast("✓ " + i18n.t("ringing"));
        setDrawer(null);
    };
    const onAck = () => {
        showToast("✓ " + i18n.t("acked"));
        setDrawer(null);
    };

    const exportCSV = () => {
        if (!data) return;
        const s = data.school;
        const rows = [["School", "Code", "Date", "Weekday", "Period", "Scheduled", "Actual", "Delay (s)", "Strikes", "Status", "Error"]];
        data.days.forEach((d) => {
            const dt = parseISO(d.date);
            data.periods.forEach((p) => {
                const e = (d.events || []).find((x) => x.period_id === p.id);
                rows.push([
                    s.name, s.code, d.date, DAYS_EN[dt.getDay()], p.name, p.time,
                    (e && e.actual_at) || "",
                    e && e.delay_sec != null ? e.delay_sec : "",
                    e ? `${e.strikes_played || 0}/${p.strikes}` : "",
                    d.holiday ? "holiday" : e ? e.status : "skipped",
                    (e && e.error_code) || "",
                ]);
            });
        });
        const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
        const a = document.createElement("a");
        a.href = URL.createObjectURL(new Blob([csv], {type: "text/csv;charset=utf-8"}));
        a.download = `digital-ghanti_${s.code}_${iso(anchor)}.csv`;
        a.click();
        showToast("✓ " + i18n.t("csv"));
    };

    const weekLabel = fmtRange(anchor, addDays(anchor, 6), i18n);
    const isThisWeek = anchor.getTime() === weekStart(new Date()).getTime();

    return (
        <div className="dgc" data-lang={lang}>
            <ConsoleStyles />

            <TopRail
                schools={schools}
                schoolId={schoolId}
                onSchoolChange={setSchoolId}
                onBackToOverall={onBackToOverall}
                weekLabel={weekLabel}
                onPrev={() => setAnchor((a) => addDays(a, -7))}
                onNext={() => setAnchor((a) => addDays(a, 7))}
                canNext={canNext}
                isThisWeek={isThisWeek}
                onThisWeek={() => setAnchor(weekStart(new Date()))}
                onRefresh={loadWeek}
                onExport={exportCSV}
                lang={lang}
                onToggleLang={toggleLang}
            />

            <div className="wrap">
                <DemoBanner visible={demoMode} i18n={i18n} />

                {data && data.school ? <SchoolContext school={data.school} i18n={i18n} /> : null}

                {data ? <WeekKpis data={data} i18n={i18n} onJump={toggleFilter} /> : null}

                <LiveBellFeed schoolId={schoolId} i18n={i18n} />

                <BellGrid
                    data={data}
                    i18n={i18n}
                    filter={filter}
                    onToggleFilter={toggleFilter}
                    onOpen={openDrawer}
                    loading={loading}
                    gridSubLabel={weekLabel}
                />

                <div className="foot">
                    <span>Digital Ghanti&reg; · Sarbatra Inc. Pvt. Ltd. · Kathmandu, Nepal</span>
                    <span>·</span>
                    <a href="https://digitalghanti.com" target="_blank" rel="noopener noreferrer">digitalghanti.com</a>
                    <span>·</span>
                    <a href="mailto:info@digitalghanti.com">info@digitalghanti.com</a>
                    <span>·</span>
                    <span className="mono">+977 982-3319821</span>
                    <span style={{marginLeft: "auto"}}>{lastFetch}</span>
                </div>
            </div>

            <EventDrawer ctx={drawer} i18n={i18n} onClose={() => setDrawer(null)} onRing={onRing} onAck={onAck} />
            <Toast msg={toastMsg} />
        </div>
    );
}

export default BellOperationsConsole;
